import { ReactNode, useMemo } from "react";
import { FormatOptionLabelMeta } from "react-select";
import { useIntl } from "react-intl";

import { DeliveryStopPod } from "api/response";
import { BaseSelect } from "components/v2/Select";
import type { SelectOption } from "components/v2/Select/types";
import {
  formatDeliveryStopMenuTimestamp,
  formatDeliveryStopSelectedLabel,
  getDeliveryStopLocationLineWithCode,
  getDeliveryStopStreetAddress,
} from "components/molecules/TopNav/common/NavIcons/MultistopProofActions/formatDeliveryStopOptionLabel";
import testIds from "constants/testIds";

/** Empty value keeps "Entire shipment" selectable through the string-based select options. */
export const ENTIRE_SHIPMENT_VALUE = "";

type StopSelectOption = SelectOption & {
  stop?: DeliveryStopPod;
};

export type TrackingStopAssociationSelectProps = Readonly<{
  stops: DeliveryStopPod[];
  value: number | null;
  onChange: (stopId: number | null) => void;
}>;

function formatStopOptionLabel(option: StopSelectOption, meta: FormatOptionLabelMeta<StopSelectOption>): ReactNode {
  if (!option.stop) {
    return option.label;
  }

  if (meta.context === "value") {
    return formatDeliveryStopSelectedLabel(option.stop);
  }

  const locationLine = getDeliveryStopLocationLineWithCode(option.stop);
  const streetAddress = getDeliveryStopStreetAddress(option.stop);
  const timestamp = formatDeliveryStopMenuTimestamp(option.stop);

  return (
    <>
      <span className="cb-select__option-primary">{`Stop ${option.stop.stop_number}`}</span>
      <span className="cb-select__option-details">
        {locationLine ? <span className="cb-select__option-location-name font-medium">{locationLine}</span> : null}
        {streetAddress ? <span className="cb-select__option-street">{streetAddress}</span> : null}
        {timestamp !== "-" ? <span className="cb-select__option-timestamp">{timestamp}</span> : null}
      </span>
    </>
  );
}

const TrackingStopAssociationSelect = function ({
  stops,
  value,
  onChange,
}: TrackingStopAssociationSelectProps) {
  const intl = useIntl();

  const options = useMemo<StopSelectOption[]>(() => {
    const entireShipmentLabel = intl.formatMessage({
      id: "tracking.entireShipment",
      defaultMessage: "Entire shipment",
    });

    return [
      { label: entireShipmentLabel, value: ENTIRE_SHIPMENT_VALUE },
      ...stops.map((stop) => ({
        label: formatDeliveryStopSelectedLabel(stop),
        value: String(stop.stop_id),
        stop,
      })),
    ];
  }, [intl, stops]);

  const handleChange = (nextValue: string | null) => {
    onChange(nextValue ? Number(nextValue) : null);
  };

  return (
    <div className="mt-3" data-testid={testIds.SHIPMENT_DASHBOARD_TRACKING_REFERENCE_STOP_SELECT}>
      <BaseSelect
        className="selectV2--delivery-stop"
        classNames={{
          menu: () => "cb-select__menu--delivery-stop",
          option: () => "text-md",
        }}
        label={intl.formatMessage({ id: "tracking.deliveryStop", defaultMessage: "Delivery stop" })}
        options={options}
        value={value != null ? String(value) : ENTIRE_SHIPMENT_VALUE}
        onChange={handleChange}
        formatOptionLabel={formatStopOptionLabel}
        isSearchable={false}
        startCursorAtEnd={false}
        maxMenuHeight={350}
        name="tracking-delivery-stop"
      />
    </div>
  );
};

export default TrackingStopAssociationSelect;
